import React, { useState, useEffect, createContext, useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import Products from './pages/Products';
import Orders from './pages/Orders';
import Transfers from './pages/Transfers';
import Reports from './pages/Reports';
import Suppliers from './pages/Suppliers';
import Locations from './pages/Locations';
import LocationDetail from './pages/LocationDetail';
import Users from './pages/Users';
import Settings from './pages/Settings';
import SupplierPortal from './pages/SupplierPortal';
import Operations from './pages/Operations';
import Tasks from './pages/Tasks';
import { resolveDefaultDashboard } from './utils/dashboardResolver';

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

const api = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
      ...options.headers
    }
  });
  const data = await res.json();
  if (!res.ok) throw { status: res.status, ...data };
  return data;
};

export { api };

function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme || 'dark');
}

function App() {
  const [user, setUser] = useState(null);
  const [theme, setThemeState] = useState(localStorage.getItem('sp_theme') || 'dark');
  const [loading, setLoading] = useState(true);

  // Apply theme on mount and change
  useEffect(() => { applyTheme(theme); }, [theme]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api('/api/auth/me')
        .then(r => {
          setUser(r.data);
          const t = r.data.theme || 'dark';
          setThemeState(t);
          localStorage.setItem('sp_theme', t);
        })
        .catch(() => { localStorage.removeItem('token'); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const r = await api('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    localStorage.setItem('token', r.data.token);
    setUser(r.data.user);
    const t = r.data.user.theme || 'dark';
    setThemeState(t);
    localStorage.setItem('sp_theme', t);
    return r;
  };

  const logout = () => {
    api('/api/auth/logout', { method: 'POST' }).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('sp_theme');
    setUser(null);
    setThemeState('dark');
  };

  const setTheme = async (newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem('sp_theme', newTheme);
    try {
      await api('/api/auth/theme', { method: 'PUT', body: JSON.stringify({ theme: newTheme }) });
    } catch (e) { console.error('Failed to save theme', e); }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--navy)' }}>
        <div style={{ color: 'var(--gold)', fontFamily: 'Lora, serif', fontSize: 24 }}>Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, theme, setTheme }}>
      <Routes>
        <Route path="/login" element={!user ? <Login /> : <Navigate to={resolveDefaultDashboard(user)} />} />
        <Route path="/portal/:token" element={<SupplierPortal />} />
        <Route path="/*" element={user ? (
          <Layout>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/operations" element={<Operations />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/products" element={<Products />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/transfers" element={<Transfers />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/suppliers" element={<Suppliers />} />
              <Route path="/locations" element={<Locations />} />
              <Route path="/locations/:id" element={<LocationDetail />} />
              <Route path="/users" element={<Users />} />
              <Route path="/settings" element={<Settings />} />
            </Routes>
          </Layout>
        ) : <Navigate to="/login" />} />
      </Routes>
    </AuthContext.Provider>
  );
}

export default App;
